import type { FilterSchema } from '@/shared/components/filter/validation'
import type { WidgetPeriods } from '../types'

import { any, last, pipe, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { useBIAnalyticQuery, zipSeries } from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'

import { Skeleton } from '@/shared/components/ui/skeleton'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

import PercentDifferenceBadge from '../../PercentDifferenceBadge'
import { Widget, WidgetContent, WidgetHeader } from '../../Widget'
import WidgetEmptyState from '../../WidgetEmptyState'
import WidgetErrorState from '../../WidgetErrorState'

const METRIC = 'optimized'

export default function NumericOptimizedPriceWidget({
	commonPeriods,
	commonFilters = [],
}: {
	commonPeriods: WidgetPeriods
	commonFilters: FilterSchema[]
}) {
	const { t } = useTranslation()

	const currency = useSelectedCurrency()
	const biCommonQuery = {
		currency,
		label: 'NumericOptimizedPrice',
		metrics: [METRIC],
		dimensions: [],
		filters: commonFilters,
		date_aggregation: null,
	}
	const biComparedPeriodQuery = useBIAnalyticQuery<number | undefined>(
		{
			...commonPeriods?.compared,
			...biCommonQuery,
		},
		{
			select: selectMetricValue,
			enabled: !!commonPeriods?.compared,
		}
	)
	const biMainPeriodQuery = useBIAnalyticQuery<number | undefined>(
		{
			...commonPeriods?.main,
			...biCommonQuery,
		},
		{
			select: selectMetricValue,
			enabled: !!commonPeriods?.main,
		}
	)
	const isLoading = any(prop('isLoading'), [
		biMainPeriodQuery,
		biComparedPeriodQuery,
	])
	const isError = any(prop('isError'), [
		biMainPeriodQuery,
		biComparedPeriodQuery,
	])
	const mainValue = biMainPeriodQuery.data
	const comparedValue = biComparedPeriodQuery.data
	const hasValueToCompare = !!mainValue && !!comparedValue

	return (
		<Widget className='relative truncate'>
			<WidgetHeader
				title={t('widget.optimized_price.title', {
					defaultValue: 'Optimized Price',
				})}
				tooltip={t('widget.optimized_price.tooltip', {
					defaultValue: 'widget.optimized_price.tooltip',
				})}
				subtitle={t('widget.optimized_price.description', {
					defaultValue: 'widget.optimized_price.description',
				})}
			/>
			<WidgetErrorState isTinted={isError} size='small' />
			<WidgetContent className='truncate'>
				{isLoading && (
					<div className='space-y-1'>
						<Skeleton className='col-span-2 h-6 w-24' />
						<Skeleton className='col-span-2 h-8 w-32' />
					</div>
				)}
				{!!mainValue && !isError && !isLoading && (
					<div className='space-y-1'>
						{hasValueToCompare && (
							<PercentDifferenceBadge
								value={(mainValue * 100) / comparedValue - 100}
							/>
						)}
						<p className='truncate text-3xl font-semibold'>
							{formatNumber(mainValue ?? 0, NumberFormats.Compact)}
						</p>
					</div>
				)}
				{!mainValue && !isError && !isLoading && (
					<WidgetEmptyState size='small' />
				)}
			</WidgetContent>
		</Widget>
	)
}
const selectMetricValue = pipe(
	(data) => zipSeries(data?.meta, data?.data),
	last,
	prop(METRIC)
)
